// src/services/flowsenseAiService.js
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (FlowSense AI Explainer) — NEW FILE ------------------------------------------------
// One client for "explain this screen" questions to FlowSense AI (POST /api/v1/flowsense-ai/ask,
// Ollama on the server). The screen passes the facts it already loaded from the backend / ML
// engine as `context`; the server adds its own live system snapshot and answers only from those.
//
//   askFlowSenseAi({ question, context, module })  → { answer, model, duration_ms, cached, truncated }
//   getFlowSenseAiStatus()                         → { status, ... } | null
//   classifyAiError(err)                           → { kind, message, retryInS }
//       kind: offline | cooldown | busy | forbidden | invalid | network | unknown
//
// The server route is admin / staff only (routes/flowsenseAssistant.js); other roles get 403 and
// the explainer shows the screen's own rule findings instead.
// --- END UPDATE ----------------------------------------------------------------------------------
import { authenticatedFetch } from './api';

const MAX_QUESTION = 1000;
const MAX_CONTEXT_CHARS = 12000; // the server clips too; keep requests small and fast

/** Drop functions/undefined, cap arrays and long strings so the context stays small. */
export const compactContext = (value, depth = 0) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value * 1000) / 1000 : null;
    if (typeof value === 'string') return value.length > 400 ? `${value.slice(0, 400)}…` : value;
    if (typeof value === 'boolean') return value;
    if (value instanceof Date) return value.toISOString();
    if (depth > 4) return null;
    if (Array.isArray(value)) return value.slice(0, 25).map((v) => compactContext(v, depth + 1));
    if (typeof value === 'object') {
        const out = {};
        Object.entries(value).slice(0, 40).forEach(([k, v]) => {
            if (typeof v === 'function' || v === undefined) return;
            out[k] = compactContext(v, depth + 1);
        });
        return out;
    }
    return null;
};

const fitContext = (context) => {
    let ctx = compactContext(context || {}) || {};
    let text = JSON.stringify(ctx);
    // shrink arrays until it fits
    const shrink = (v, cap) => (Array.isArray(v) ? v.slice(0, cap).map((x) => shrink(x, cap))
        : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, shrink(x, cap)])) : v);
    for (let cap = 12; text.length > MAX_CONTEXT_CHARS && cap >= 3; cap = Math.floor(cap / 2)) {
        ctx = shrink(ctx, cap);
        text = JSON.stringify(ctx);
    }
    return ctx;
};

const statusOf = (err) => err?.status ?? err?.statusCode ?? err?.response?.status ?? err?.data?.status ?? null;
const codeOf = (err) => err?.code ?? err?.data?.code ?? err?.response?.data?.code ?? err?.body?.code ?? null;

export const classifyAiError = (err) => {
    const status = statusOf(err);
    const code = String(codeOf(err) || '');
    const msg = String(err?.response?.data?.message || err?.data?.message || err?.message || '');
    const retry = err?.data?.retry_in_s ?? err?.response?.data?.retry_in_s ?? null;
    if (code === 'AI_COOLDOWN') return { kind: 'cooldown', message: msg || 'FlowSense AI is recovering.', retryInS: retry };
    if (code === 'AI_BUSY' || status === 429) return { kind: 'busy', message: 'FlowSense AI is answering another question — try again in a moment.' };
    if (code === 'AI_OFFLINE' || code === 'AI_UNAVAILABLE' || status === 503) return { kind: 'offline', message: 'FlowSense AI is offline right now.' };
    if (status === 401 || status === 403 || /forbidden|insufficient|not authori[sz]ed|permission/i.test(msg)) {
        return { kind: 'forbidden', message: 'AI explanations are available to admin and office staff accounts.' };
    }
    if (status === 400 || status === 422) return { kind: 'invalid', message: msg || 'The question could not be sent.' };
    if (!status && /network|failed to fetch|timeout|abort/i.test(msg)) return { kind: 'network', message: 'No connection to the FlowSense server.' };
    return { kind: 'unknown', message: msg || 'FlowSense AI could not answer.' };
};

/**
 * @param {{question:string, context?:object, module?:string, signal?:AbortSignal}} p
 */
export const askFlowSenseAi = async ({ question, context, module, signal } = {}) => {
    const q = String(question || '').trim().slice(0, MAX_QUESTION);
    if (!q) throw Object.assign(new Error('Ask a question first.'), { status: 400 });
    const body = { question: q, context: fitContext(context) };
    if (module) body.module = String(module).slice(0, 40);
    const res = await authenticatedFetch('/flowsense-ai/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        ...(signal ? { signal } : {}),
    });
    // authenticatedFetch may resolve with an error body instead of throwing
    if (res && res.success === false) {
        throw Object.assign(new Error(res.message || 'FlowSense AI could not answer.'), { code: res.code, data: res });
    }
    const data = res?.data || res || {};
    if (!data.answer) throw Object.assign(new Error('FlowSense AI returned an empty answer.'), { code: 'AI_UNAVAILABLE' });
    return data;
};

export const getFlowSenseAiStatus = async () => {
    try {
        const res = await authenticatedFetch('/flowsense-ai/status');
        return res?.data || res || null;
    } catch (_) {
        return null;
    }
};

const flowsenseAiService = { askFlowSenseAi, getFlowSenseAiStatus, classifyAiError, compactContext };
export default flowsenseAiService;
